import { TOOLBAR_DROPDOWN_PT, TOOLBAR_LABEL_CLASSES } from '@/components/projects/toolbarDropdown';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import { Dropdown } from 'primereact/dropdown';
import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface NewProjectRuleSetProps {
  ruleSets: RuleSetListItem[];
  /** The remembered choice; null only until the list has loaded. */
  value: string | null;
  onChange: (name: string) => void;
  isLoading: boolean;
  /** Translation key of the reason the list could not be loaded, if any. */
  error: string | null;
}

/**
 * Which rule set the next project is created with.
 *
 * It sits beside the upload controls rather than on the project page because a project's
 * rule set decides how its holes are machined, so it has to be settled before the file is
 * dropped — afterwards the procedures on record were already made with it.
 *
 * The control stays on screen even with nothing to pick: a picker that vanishes when the
 * backend is unreachable reads as a missing feature rather than as a failed request.
 */
export const NewProjectRuleSet: FC<NewProjectRuleSetProps> = ({ ruleSets, value, onChange, isLoading, error }) => {
  const { t } = useTranslation();
  const dropdownId = useId();

  const options = ruleSets.map((ruleSet) => ({
    label: ruleSet.title || ruleSet.name,
    value: ruleSet.name,
  }));
  const unavailable = options.length === 0;

  let placeholder = t('ruleSets.selectPlaceholder');
  if (error) placeholder = t(error);
  else if (isLoading) placeholder = t('common.loading');
  else if (unavailable) placeholder = t('ruleSets.empty');

  return (
    <div className="flex items-center gap-2" title={unavailable ? placeholder : t('ruleSets.newProjectHint')}>
      <label htmlFor={dropdownId} className={TOOLBAR_LABEL_CLASSES}>
        {t('ruleSets.newProjectLabel')}
      </label>
      <Dropdown
        inputId={dropdownId}
        value={value}
        options={options}
        onChange={(e) => onChange(e.value as string)}
        placeholder={placeholder}
        disabled={unavailable}
        className="w-48"
        pt={TOOLBAR_DROPDOWN_PT}
      />
    </div>
  );
};
